import React, { useState, useEffect } from "react";
import NavHeader from "../nav/NavHeader";
import SideMenu from "../nav/Sidemenu";
import ReportRegistrationsFrom from "../reportRegistrations/ReportRegistrationsFrom";
import ReportManagementTable from "../reportRegistrations/ReportManagementTable";
import "../../assets/css/reportRegistrations.css";
import PageHeader from "../layout/PageHeader";
import TabStrip from "../layout/TabStrip";
import RankedTable from "../layout/RankedTable";
import AppButton from "../layout/AppButton";
import ToastNotification from "../layout/ToastNotification";
import ConfirmDialog from "../layout/ConfirmDialog";
import { useReportAdmin } from "../../hooks/useReportAdmin";
import EditReportModal from "../reportRegistrations/EditReportModal";

const ReportAdministration = () => {
    const { error, fetchReports: fetchRawReports, updateReport, deleteReport, clearError } = useReportAdmin();
    const [activeTab, setActiveTab] = useState("management");
    const [searchText, setSearchText] = useState("");
    const [reports, setReports] = useState([]);
    const [tableLoading, setTableLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editReportData, setEditReportData] = useState(null);
    const [updating, setUpdating] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
    const [confirmDialog, setConfirmDialog] = useState({ open: false, reportId: null });

    const showToast = (message, severity = "success") =>
        setToast({ open: true, message, severity });
    const handleCloseToast = () => setToast((prev) => ({ ...prev, open: false }));

    // Fetch all reports on mount
    useEffect(() => {
        loadReports();
    }, []);

    const loadReports = async () => {
        setTableLoading(true);
        try {
            const data = await fetchRawReports();
            const raw = data?.data ?? (Array.isArray(data) ? data : []);
            setReports(raw.map(report => ({
                id: report.id,
                reportName: report.report_name,
                reportId: report.report_id.toString(),
                groupId: report.group_id,
                datasetId: report.dataset_id || "",
                createdBy: report.created_by || "",
                createdAt: report.create_date_time ? formatDateTime(report.create_date_time) : "",
                rlsEnabled: report.rls_enabled,
                reportDescription: report.report_description || "",
                reportUrl: report.url,
                status: report.status,
                lastUpdateTime: report.last_update_time,
            })));
        } catch {
            showToast("Failed to fetch reports", "error");
            setReports([]);
        } finally {
            setTableLoading(false);
        }
    };

    const formatDateTime = (dateString) => {
        try {
            const date = new Date(dateString);
            return date.toLocaleString("en-US", {
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        } catch {
            return dateString;
        }
    };

    const buildReportUrl = (reportId, groupId) =>
        `https://app.powerbi.com/reportEmbed?reportId=${reportId || ""}&groupId=${groupId || ""}`;

    const handleEditReport = (report) => {
        setEditReportData({
            originalReportId: report.reportId,
            reportName: report.reportName,
            reportId: report.reportId,
            groupId: report.groupId,
            datasetId: report.datasetId,
            rlsEnabled: String(report.rlsEnabled),
            reportDescription: report.reportDescription,
            reportUrl: buildReportUrl(report.reportId, report.groupId),
            id: report.id,
        });
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditReportData(null);
    };

    const handleEditChange = (event) => {
        const { name, value } = event.target;
        setEditReportData((prevData) => {
            if (!prevData) {
                return prevData;
            }

            const updatedData = { ...prevData, [name]: value };
            return {
                ...updatedData,
                reportUrl: buildReportUrl(updatedData.reportId, updatedData.groupId),
            };
        });
    };

    const handleUpdateReport = async (event) => {
        event.preventDefault();
        if (!editReportData) return;

        setUpdating(true);
        try {
            await updateReport(
                editReportData.originalReportId, {
                report_name: editReportData.reportName,
                group_id: editReportData.groupId,
                dataset_id: editReportData.datasetId,
                rls_enabled: editReportData.rlsEnabled === "true",
                report_description: editReportData.reportDescription,
                id: editReportData.id,
                report_guid: editReportData.reportId, // Assuming reportId is used as report_guid
            });
            await loadReports();
            showToast("Report updated successfully");
            handleCloseEditModal();
        } catch {
            showToast("Failed to update report", "error");
        } finally {
            setUpdating(false);
        }
    };

    const handleDeleteReport = (reportId) => {
        if (!reportId) {
            showToast("Invalid report ID", "error");
            return;
        }
        setConfirmDialog({ open: true, reportId });
    };

    const handleConfirmDeleteReport = async () => {
        const reportId = confirmDialog.reportId;
        if (!reportId) return;

        setDeleting(true);
        try {
            await deleteReport(reportId);
            await loadReports();
            showToast("Report deleted successfully");
            setConfirmDialog({ open: false, reportId: null });
        } catch {
            showToast("Failed to delete report", "error");
        } finally {
            setDeleting(false);
        }
    };

    const handleReportRegistered = async () => {
        await loadReports();
        setActiveTab("management");
        showToast("Report registered successfully");
    };

    const filteredReports = reports.filter((report) => {
        const query = searchText.toLowerCase();
        return (
            report.reportName.toLowerCase().includes(query) ||
            report.reportId.toLowerCase().includes(query) ||
            report.groupId.toLowerCase().includes(query)
        );
    });

    return (
        <div>
            <NavHeader />
            <SideMenu />
            <div style={{ position: "relative" }} id="main">
                <PageHeader
                    title="Report Administration"
                    subtitle="Manage and register Power BI reports, including metadata, dataset mapping, and RLS settings."
                />

                {error && (
                    <div className="alert alert-danger alert-dismissible fade show mx-3" role="alert">
                        {error}
                        <AppButton
                            variant="outline"
                            onClick={clearError}
                            style={{ padding: "2px 10px", float: "right", lineHeight: 1 }}
                            title="Dismiss"
                        >
                            ×
                        </AppButton>
                    </div>
                )}

                <div className="pb-4">
                    <TabStrip
                        tabs={[
                            { label: "Report Management", value: "management" },
                            { label: "Report Registrations", value: "registrations" },
                        ]}
                        active={activeTab}
                        onChange={setActiveTab}
                    />
                </div>

                {activeTab === "management" ? (
                    tableLoading ? (
                        <div className="pb-4">
                            <RankedTable
                                columns={[
                                    { label: "Report Name" }, { label: "Report ID" }, { label: "Group ID" },
                                    { label: "Dataset ID" }, { label: "Created By" }, { label: "Created At" },
                                    { label: "RLS", align: "center" }, { label: "Actions", align: "center" },
                                ]}
                                rows={Array.from({ length: 6 }, (_, i) => ({ _skeleton: true, _idx: i }))}
                                emptyText=""
                                renderRow={(row) => (<>
                                    {[1, 2, 3, 4, 5, 6, 7, 8].map(j => (
                                        <td key={j} className="px-5 py-3">
                                            <div style={{ height: "13px", background: "#f1f5f9", borderRadius: "6px", width: j === 7 ? "50%" : j === 8 ? "70%" : "85%", animation: "pulse 1.5s ease-in-out infinite" }} />
                                        </td>
                                    ))}
                                </>)}
                            />
                        </div>
                    ) : (
                        <ReportManagementTable
                            searchText={searchText}
                            setSearchText={setSearchText}
                            filteredReports={filteredReports}
                            onEditReport={handleEditReport}
                            onDeleteReport={handleDeleteReport}
                        />
                    )
                ) : (
                    <ReportRegistrationsFrom onReportRegistered={handleReportRegistered} />
                )}

                <EditReportModal
                    open={showEditModal}
                    reportData={editReportData}
                    onClose={handleCloseEditModal}
                    onChange={handleEditChange}
                    onSubmit={handleUpdateReport}
                    updating={updating}
                />
                <ToastNotification
                    open={toast.open}
                    message={toast.message}
                    severity={toast.severity}
                    onClose={handleCloseToast}
                />
                <ConfirmDialog
                    open={confirmDialog.open}
                    title="Delete Report"
                    message="Are you sure you want to delete this report? This action cannot be undone."
                    confirmLabel="Delete"
                    loading={deleting}
                    loadingLabel="Deleting..."
                    onConfirm={handleConfirmDeleteReport}
                    onClose={() => {
                        if (!deleting) {
                            setConfirmDialog({ open: false, reportId: null });
                        }
                    }}
                />
            </div>
        </div>
    );
};

export default ReportAdministration;
